import { useState, type FormEvent } from "react";
import type { CmmdProjectMilestone } from "@cmmd-forge/contracts";
import { CalendarIcon, ChevronRightIcon, DiamondIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ProjectTaskMilestoneIcon } from "./ProjectTaskMilestoneBoardIcons";
import type { ProjectMilestonesController } from "./useProjectMilestones";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";

const milestoneDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

const MILESTONE_STATUS_LABELS: Readonly<Record<CmmdProjectMilestone["status"], string>> = {
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  overdue: "Overdue",
};

interface ProjectMilestoneOverviewProps {
  readonly controller: ProjectMilestonesController;
  readonly onClose: () => void;
  readonly onSelectMilestone: (milestoneId: number) => void;
}

export function ProjectMilestoneOverview(props: ProjectMilestoneOverviewProps) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Project milestones</DialogTitle>
        <DialogDescription>
          Create release checkpoints, then add their outcome and exit criteria.
        </DialogDescription>
      </DialogHeader>
      <DialogPanel className="space-y-5 px-4 md:px-6">
        <MilestoneQuickCreate
          controller={props.controller}
          onCreated={(milestone) => props.onSelectMilestone(milestone.id)}
        />
        <MilestoneOverviewError error={props.controller.error} />
        <MilestoneList
          milestones={props.controller.milestones}
          loading={props.controller.loading}
          onSelectMilestone={props.onSelectMilestone}
        />
      </DialogPanel>
      <DialogFooter variant="bare">
        <Button
          type="button"
          variant="outline"
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          onClick={props.onClose}
        >
          Done
        </Button>
      </DialogFooter>
    </>
  );
}

function MilestoneQuickCreate(props: {
  readonly controller: ProjectMilestonesController;
  readonly onCreated: (milestone: CmmdProjectMilestone) => void;
}) {
  const [title, setTitle] = useState("");
  const [targetDate, setTargetDate] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) return;
    const milestone = await props.controller.create({
      title: trimmedTitle,
      ...(targetDate.length > 0 ? { targetDate } : {}),
    });
    if (milestone == null) return;
    props.onCreated(milestone);
  };

  return (
    <form className="space-y-3" onSubmit={(event) => void submit(event)}>
      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_10rem_auto] md:items-end">
        <div className="space-y-1.5">
          <Label htmlFor="new-milestone-title" className="flex items-center gap-1.5">
            <DiamondIcon className="size-4 text-muted-foreground" aria-hidden />
            Milestone title
          </Label>
          <Input
            id="new-milestone-title"
            aria-label="New milestone title"
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            placeholder="Internal candidate ready"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-milestone-date" className="flex items-center gap-1.5">
            <CalendarIcon className="size-4 text-muted-foreground" aria-hidden />
            Target date
          </Label>
          <Input
            id="new-milestone-date"
            aria-label="New milestone target date"
            type="date"
            value={targetDate}
            onChange={(event) => setTargetDate(event.currentTarget.value)}
          />
        </div>
        <Button
          type="submit"
          disabled={title.trim().length === 0 || props.controller.mutating}
          className={`w-full md:w-auto ${RESPONSIVE_TEXT_BUTTON_CLASS}`}
        >
          {props.controller.mutating ? "Creating…" : "Create milestone"}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground md:text-xs">
        You’ll add the release brief and planning details next.
      </p>
    </form>
  );
}

function MilestoneList(props: {
  readonly milestones: readonly CmmdProjectMilestone[];
  readonly loading: boolean;
  readonly onSelectMilestone: (milestoneId: number) => void;
}) {
  if (props.loading) {
    return <p className="py-6 text-center text-sm text-muted-foreground">Loading milestones…</p>;
  }
  if (props.milestones.length === 0) {
    return (
      <p className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        No milestones yet.
      </p>
    );
  }
  return (
    <div className="divide-y divide-border border-y border-border" aria-label="Existing milestones">
      {props.milestones.map((milestone) => (
        <MilestoneRow
          key={milestone.id}
          milestone={milestone}
          onSelect={() => props.onSelectMilestone(milestone.id)}
        />
      ))}
    </div>
  );
}

function MilestoneRow(props: {
  readonly milestone: CmmdProjectMilestone;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={props.onSelect}
      className="flex min-h-touch w-full min-w-0 items-center gap-3 px-2 py-2 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:min-h-11"
    >
      <ProjectTaskMilestoneIcon status={props.milestone.status} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {props.milestone.title}
        </span>
        <span className="block truncate text-sm text-muted-foreground md:text-xs">
          {milestoneSummary(props.milestone)}
        </span>
      </span>
      <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
}

function milestoneSummary(milestone: CmmdProjectMilestone): string {
  const status = MILESTONE_STATUS_LABELS[milestone.status];
  const targetDate = formatMilestoneDate(milestone.endDate);
  return targetDate != null ? `${status} · Target ${targetDate}` : status;
}

function formatMilestoneDate(value: string | null): string | null {
  if (value == null) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : milestoneDateFormatter.format(date);
}

function MilestoneOverviewError(props: { readonly error: string | null }) {
  return props.error != null ? (
    <p role="alert" className="text-sm text-destructive">
      {props.error}
    </p>
  ) : null;
}
